import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { StatusKey } from '../../../../../enum';
import type { Measurement, ScoreDetail } from '../../../../../models/client';
import type { ErrorMessage } from '../../../../../models/error';
import type { AsyncThunkConfig } from '../../../../../models/slice';
import type { FilteredInfo, RequestParams, TableChange } from '../../../../../models/table';
import { SortedInfo } from '../../../../../models/table';
import { RaygunErrorHandlerService } from '../../../../../service/raygun.service';
import { removeEmpty } from '../../../../../service/table.service';

const { logError } = RaygunErrorHandlerService();

type FieldSelection = {
  img: string;
  icon: string;
  value: string;
  name_alias: string;
  colour_code: string;
  numeric_value: number;
};

export type Field = {
  remarks: string | null;
  field_id: string;
  field_name: string;
  field_type: string;
  selections: FieldSelection[];
  field_short_name: string | null;
  field_description: string;
  hide_in_radial: boolean;
};

export type ClientJournal = {
  submission_id: string | null;
  client_journal_id: string | null;
  product_id: string | null;
  product_name: string | null;
  product_short_name: string | null;
  scale_score_for_display: number | null;
  order: number | null;
  created_date: string | null;
  score: number | null;
  status: 'high_risk' | 'medium_risk' | 'low_risk' | 'inactive' | 'incomplete' | null;
  jumps: number | null;
  score_details: ScoreDetail | null;
  needs_callback: string;
  fields: Field[] | null;
};

export type ClientEvent = {
  total_count: number;
  id: string;
  type: 'submission' | 'intervention' | 'feedback' | 'archive' | 'incomplete';
  measurements: Measurement | null;
  products: (string | null)[] | null;
  client_id: string;
  diary: string | null;
  created_date: string;
  submission_time: string;

  client_journals: ClientJournal[] | null;

  score: number | null;
  mood: string | null;
  mood_id: string | null;
  sentiment: string | null;
  status: StatusKey | 'Intervention' | 'Feedback' | 'Archive';
  details: Details | null;

  clinician_name: string | null;
  clinician_message: string | null;
};

type ClientEventsSliceType = {
  clientEvents: ClientEvent[];
  loadingClientEvents: boolean;
  requestClientEventsParams: RequestParams<ClientEvent>;
};

export type ClientEntry = ClientJournal & {
  id: string;
  type: ClientEvent['type'];
  measurements: Measurement | null;
  product: string | null;
  client_id: string;
  diary: string | null;
  submission_time: string;
  mood: string | null;
  mood_id: string | null;
  sentiment: string | null;
  details: Details | null;
};

type Details = {
  measurements: Measurement | null;
  diary: string | null;
  details: string | null;
  clinician_first_name: string;
  clinician_last_name: string;
  emoji_hex_code: string;
  priority_id: string;
  case_status_id: string;
  follow_up_by?: string | null;
  call_start_time?: string | null;
  call_end_time?: string | null;
  call_result?: string | null;
};

export const get_client_entry = (e: ClientEvent, cj: ClientJournal): ClientEntry => {
  return {
    ...cj,
    id: e.id,
    type: e.type,
    measurements: e.measurements,
    product: cj.product_short_name,
    client_id: e.client_id,
    diary: e.diary,
    submission_time: e.submission_time,
    mood: e.mood,
    mood_id: e.mood_id,
    sentiment: e.sentiment,
    details: e.details,
  };
};

const initialState: ClientEventsSliceType = {
  clientEvents: [],
  loadingClientEvents: false,
  requestClientEventsParams: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<ClientEvent>('submission_time', 'descend'),
    filteredInfo: {},
  },
};

export const fetchClientEvents = createAsyncThunk<ClientEvent[], string, AsyncThunkConfig>(
  'clientEvents/fetchClientEvents',
  async (client_id: string, thunkAPI) => {
    try {
      const {
        clientSubmissionsSlice: {
          requestClientEventsParams: { pagination, sortedInfo, filteredInfo },
        },
      } = thunkAPI.getState();
      const response = await axios.post(
        'v3_client_submission',
        {
          client_id,
          pagination,
          sortedInfo,
          filteredInfo,
        },
        { signal: thunkAPI.signal },
      );
      return (response as unknown as ClientEvent[]) ?? [];
    } catch (e) {
      logError(e, ['clientEventsSlice', 'fetchClientEvents']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const clientSubmissionsSlice = createSlice({
  name: 'clientSubmissions',
  initialState,
  reducers: {
    handleClientEventsChange: (state, action: PayloadAction<TableChange<ClientEvent>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<ClientEvent>;
      if (!sorter.order) {
        sorter.order = null;
      }
      state.requestClientEventsParams.pagination = action.payload.pagination;
      state.requestClientEventsParams.sortedInfo = sorter;
      state.requestClientEventsParams.filteredInfo = removeEmpty(filters);
    },
    clearFilter: (state) => {
      state.requestClientEventsParams.pagination = initialState.requestClientEventsParams.pagination;
      state.requestClientEventsParams.sortedInfo = initialState.requestClientEventsParams.sortedInfo;
      state.requestClientEventsParams.filteredInfo = initialState.requestClientEventsParams.filteredInfo;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientEvents.pending, (state, _action) => {
        state.loadingClientEvents = true;
      })
      .addCase(fetchClientEvents.fulfilled, (state, action) => {
        state.clientEvents = action.payload;
        state.loadingClientEvents = false;
      })
      .addCase(fetchClientEvents.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingClientEvents = false;
        }
      });
  },
});

export const { handleClientEventsChange, clearFilter } = clientSubmissionsSlice.actions;
